import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGlobe,
    faLock,
    faEnvelope,
    faPenNib,
    faDownload,
    faUserCheck,
    faUsers,
    faPlus,
    faRotate,
    faChevronRight,
    faLink,
    faCopy,
} from '@fortawesome/free-solid-svg-icons';
import brandIcon from '../icon.png';
import { GlobalLinkKeys } from '../rtdbKeys';
import {
    entityRef,
    globalUrl,
    setAccessMode,
    resetGlobalLink,
} from '../shareTarget';
import Toggle from './Toggle';
import ManageAccess from './ManageAccess';
import ShareLinkComposer from './ShareLinkComposer';

// Universal share sheet for projects + tracks. Mirrors iOS ShareSheetVC.
// Same UI for both targets — settings live on /links/global (server is
// authoritative; CFs do the writing). Toggle changes auto-save through
// setProjectAccessMode / setTrackAccessMode. All three modes (Private /
// Invite only / Public) are real server states — see SHARING_MODEL.md §1.
const MODE_ICON = { private: faLock, inviteOnly: faEnvelope, public: faGlobe };
const MODE_TITLE = { private: 'Private', inviteOnly: 'Invite only', public: 'Public link' };
const modeSubtitle = (mode, kind) => {
    if (mode === 'public') return `Anyone with the link can open this ${kind}`;
    if (mode === 'inviteOnly') return `Generate invite links to share this ${kind} with specific people`;
    return `Only artist members can open this ${kind}`;
};

const ShareModal = ({ target, onClose }) => {
    const [view, setView] = useState('main'); // main | manage | composer
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // /links/global subtree: { currentSlug, settings: {...}, history: {...} }
    const [link, setLink] = useState({});
    const [cachedUrl, setCachedUrl] = useState(null);
    // public→private confirmation gate. iOS uses an action sheet with two
    // explicit choices (keep current viewers vs remove all); the dropdown
    // alone can't express both, so when the user picks Private we surface
    // an inline confirm panel and delay the actual setAccessMode call.
    const [pendingPrivate, setPendingPrivate] = useState(false);

    const settings = link[GlobalLinkKeys.settings] || {};
    const slug = link[GlobalLinkKeys.currentSlug] || null;
    const rawMode = settings[GlobalLinkKeys.accessMode];
    const serverMode = rawMode === 'public' || rawMode === 'inviteOnly'
        ? rawMode : 'private';
    const displayMode = serverMode;
    const allowEditing = !!settings[GlobalLinkKeys.allowEditing];
    const allowDownloads = !!settings[GlobalLinkKeys.allowDownloads];
    const requireAccount = !!settings[GlobalLinkKeys.requireAccount];
    const url = cachedUrl || (serverMode === 'public' ? globalUrl(target, slug) : null);

    useEffect(() => {
        if (!target) return;
        const ref = entityRef(target).child('links/global');
        const handler = ref.on('value', (snap) => setLink(snap.val() || {}));
        return () => ref.off('value', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target?.kind, target?.artistId, target?.projectId, target?.trackId]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        if (pendingPrivate && displayMode === 'private') {
            setPendingPrivate(false);
        }
    }, [displayMode, pendingPrivate]);

    const pushSettings = async (overrides = {}) => {
        if (busy) return false;
        setBusy(true);
        setError('');
        const merged = {
            allowEditing,
            allowDownloads,
            requireAccount,
            ...overrides.settings,
        };
        const mode = overrides.mode || serverMode;
        try {
            const res = await setAccessMode(target, mode, merged);
            const newSlug = res?.slug;
            if (newSlug && mode === 'public') {
                setCachedUrl(globalUrl(target, newSlug));
            }
            if (mode !== 'public') setCachedUrl(null);
            return true;
        } catch (err) {
            setError(err?.message || "Couldn't save settings.");
            return false;
        } finally {
            setBusy(false);
        }
    };

    const requestModeChange = (next) => {
        if (next === displayMode) return;
        if (next === 'private') {
            setPendingPrivate(true);
            return;
        }
        // inviteOnly preserves grants + tokens server-side; public allocates
        // (or reuses) the slug. Both round-trip through the CF.
        pushSettings({ mode: next });
    };

    const confirmGoPrivate = () => {
        setPendingPrivate(false);
        pushSettings({ mode: 'private' });
    };

    const handleResetLink = async () => {
        const word = target.kind === 'track' ? 'track' : 'project';
        if (!window.confirm(`Reset ${word} link? Anyone with the current link won't be able to use it anymore.`)) return;
        setBusy(true);
        setError('');
        try {
            const res = await resetGlobalLink(target);
            const newSlug = res?.slug;
            if (newSlug && serverMode === 'public') {
                setCachedUrl(globalUrl(target, newSlug));
            }
        } catch (err) {
            setError(err?.message || "Couldn't reset link.");
        } finally {
            setBusy(false);
        }
    };

    const handleCopy = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
        } catch (_e) { /* clipboard blocked; ignore */ }
    };

    if (!target) return null;

    if (view === 'manage') {
        return <ManageAccess target={target} accessMode={serverMode} onBack={() => setView('main')} onClose={onClose} />;
    }
    if (view === 'composer') {
        return <ShareLinkComposer target={target} onBack={() => setView('main')} onClose={onClose} />;
    }

    const eyebrowLabel = target.kind === 'track' ? 'SHARE TRACK' : 'SHARE';
    const resetLabel = target.kind === 'track' ? 'Reset track link' : 'Reset project link';

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-eyebrow eyebrow-share">
                    <FontAwesomeIcon icon={faLink} />
                    <span>{eyebrowLabel}</span>
                </div>
                <div className="modal-title">{target.name || 'Untitled'}</div>
                {target.subtitle && (
                    <div className="modal-subtitle">{target.subtitle}</div>
                )}

                <div className={`share-mode-card mode-${displayMode}`}>
                    <div className="share-mode-card-icon">
                        <FontAwesomeIcon icon={MODE_ICON[displayMode]} />
                    </div>
                    <div className="share-mode-card-text">
                        <div className="share-mode-card-title">
                            {MODE_TITLE[displayMode]}
                        </div>
                        <div className="share-mode-card-sub">
                            {modeSubtitle(displayMode, target.kind)}
                        </div>
                    </div>
                    <select
                        className="share-select"
                        value={pendingPrivate ? 'private' : displayMode}
                        disabled={busy}
                        onChange={(e) => requestModeChange(e.target.value)}
                    >
                        <option value="private">Private</option>
                        <option value="inviteOnly">Invite only</option>
                        <option value="public">Public</option>
                    </select>
                </div>

                {pendingPrivate && (
                    <div className="share-confirm-card">
                        <div className="share-confirm-title">Make {target.kind} private?</div>
                        <div className="share-confirm-sub">
                            All invite links will stop working and everyone with access
                            will be removed.
                        </div>
                        <div className="share-confirm-actions">
                            <button
                                className="btn-ghost"
                                onClick={() => setPendingPrivate(false)}
                                disabled={busy}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary btn-danger"
                                onClick={confirmGoPrivate}
                                disabled={busy}
                            >
                                Make private
                            </button>
                        </div>
                    </div>
                )}

                {displayMode === 'public' && !pendingPrivate && (
                    <>
                        <div className="share-row indent">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${allowEditing ? 'is-on' : ''}`}>
                                    <FontAwesomeIcon icon={faPenNib} />
                                </span>
                                Allow editing
                            </span>
                            <Toggle
                                checked={allowEditing}
                                disabled={busy}
                                accent="blue"
                                onChange={(e) => {
                                    const next = e.target.checked;
                                    if (next && !window.confirm('Allow editing? Anyone signed in with the link will be able to add and edit tracks, lyrics, ideas, and art.')) {
                                        return;
                                    }
                                    // Editing without a known account = anonymous
                                    // writes by anyone with the link. Force-pin
                                    // requireAccount on so every editor is at
                                    // least an identifiable Demodeck user.
                                    const overrides = { allowEditing: next };
                                    if (next) overrides.requireAccount = true;
                                    pushSettings({ settings: overrides });
                                }}
                            />
                        </div>
                        <div className="share-row indent">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${allowDownloads ? 'is-on' : ''}`}>
                                    <FontAwesomeIcon icon={faDownload} />
                                </span>
                                Allow downloads
                            </span>
                            <Toggle
                                checked={allowDownloads}
                                disabled={busy}
                                onChange={(e) => pushSettings({
                                    settings: { allowDownloads: e.target.checked },
                                })}
                            />
                        </div>
                        <div className="share-row indent">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${requireAccount ? 'is-info' : ''}`}>
                                    <FontAwesomeIcon icon={faUserCheck} />
                                </span>
                                Require account
                                {allowEditing && (
                                    <span className="share-row-hint">Required while editing is on</span>
                                )}
                            </span>
                            <Toggle
                                checked={requireAccount || allowEditing}
                                disabled={busy || allowEditing}
                                accent="blue"
                                onChange={(e) => pushSettings({
                                    settings: { requireAccount: e.target.checked },
                                })}
                            />
                        </div>
                        {url && (
                            <div className="share-link-preview">
                                <FontAwesomeIcon icon={faLink} className="share-link-preview-icon" />
                                <code>{url}</code>
                                <button className="btn-ghost" onClick={handleCopy} disabled={busy}>
                                    <FontAwesomeIcon icon={faCopy} /> Copy
                                </button>
                            </div>
                        )}
                    </>
                )}

                {displayMode === 'inviteOnly' && !pendingPrivate && (
                    <div className="share-mode-helper">
                        Tokenized invite links let you set per-link permissions, passwords,
                        expiry, and max uses. The {target.kind} stays private to anyone
                        without an invite.
                    </div>
                )}

                {displayMode === 'private' && !pendingPrivate && (
                    <div className="share-mode-helper">
                        Only artist members can open this {target.kind}. Switch to Invite
                        only or Public to share it.
                    </div>
                )}

                {error && <div className="new-project-error">{error}</div>}

                {displayMode !== 'private' && (
                    <div className="share-secondary-actions">
                        {displayMode === 'inviteOnly' && (
                            <>
                                <button
                                    className="share-action-btn is-primary"
                                    onClick={() => setView('composer')}
                                    disabled={busy}
                                >
                                    <span className="share-action-icon icon-share">
                                        <FontAwesomeIcon icon={faPlus} />
                                    </span>
                                    <span>Generate invite link</span>
                                    <FontAwesomeIcon icon={faChevronRight} className="share-action-arrow" />
                                </button>
                                <button className="share-action-btn" onClick={() => setView('manage')} disabled={busy}>
                                    <span className="share-action-icon icon-blue">
                                        <FontAwesomeIcon icon={faUsers} />
                                    </span>
                                    <span>Manage links & access</span>
                                    <FontAwesomeIcon icon={faChevronRight} className="share-action-arrow" />
                                </button>
                            </>
                        )}
                        {displayMode === 'public' && (
                            <button className="share-action-btn" onClick={handleResetLink} disabled={busy}>
                                <span className="share-action-icon icon-warn">
                                    <FontAwesomeIcon icon={faRotate} />
                                </span>
                                <span>{resetLabel}</span>
                                <FontAwesomeIcon icon={faChevronRight} className="share-action-arrow" />
                            </button>
                        )}
                    </div>
                )}

                <div className="new-project-actions">
                    <button className="btn-ghost" onClick={onClose} disabled={busy}>Done</button>
                </div>

                <div className="brand-footer brand-footer-quiet" aria-hidden="true">
                    <img src={brandIcon} alt="" className="brand-footer-logo" />
                    <span className="brand-footer-name">demodeck</span>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
